import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus, SendIcon, Trash2, SparkleIcon } from "lucide-react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { FieldArrayWithId, UseFormReturn } from "react-hook-form";
import createAxios from "@/lib/axios";
import { toast } from "sonner";

interface Question {
  question: string;
  reference_answer: string;
  rubrics: string;
  guidelines: string;
  marks: number;
}

interface QuestionSet {
  name: string;
  questions: Question[];
}

const questionSchema = z.object({
  question: z.string().min(1, "Question is required"),
  reference_answer: z.string().min(1, "Reference answer is required"),
  rubrics: z.string(),
  guidelines: z.string(),
  marks: z.number().min(1, "Marks must be at least 1"),
});

const setSchema = z.object({
  name: z.string().min(1, "Name is required"),
  questions: z
    .array(questionSchema)
    .min(1, "At least one question is required"),
});

export const Route = createFileRoute("/dashboard/evaluate/question-set/new")({
  component: () => (
    <NewQuestionSet
      defaultValues={{
        name: "",
        questions: [
          {
            question: "",
            reference_answer: "",
            rubrics: "",
            guidelines: "",
            marks: 5,
          },
        ],
      }}
      onSubmit={handleSubmit}
    />
  ),
});

interface QuestionProps {
  index: number;
  field: FieldArrayWithId<QuestionSet, "questions">;
  form: UseFormReturn<QuestionSet>;
  remove: (index: number) => void;
}

async function handleSubmit(
  data: QuestionSet,
  onSuccess?: (id: string) => void,
) {
  try {
    const client = await createAxios();
    const requestBody = {
      name: data.name,
      questions: data.questions.map((question) => ({
        question: question.question,
        reference_answer: question.reference_answer,
        rubrics: question.rubrics + "\n\n# Specific\n\n" + question.guidelines,
        marks: question.marks,
      })),
    };

    const response = await client.post<{ id: string }>(
      `${import.meta.env.VITE_BACKEND_URL}/question-set`,
      requestBody,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const questionSet = response.data;
    toast.success("Question set created successfully");
    if (questionSet.id && onSuccess) {
      onSuccess(questionSet.id);
    }
    return questionSet;
  } catch (error) {
    toast.error("Failed to create question set");
    throw error;
  }
}

function NewQuestionSet({
  defaultValues,
}: {
  defaultValues?: QuestionSet;
  onSubmit: (data: QuestionSet) => Promise<{ id: string }>;
}) {
  const form = useForm<QuestionSet>({
    resolver: zodResolver(setSchema),
    defaultValues: defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "questions",
  });

  const navigate = useNavigate();

  return (
    <div className="size-full bg-card px-4 py-8 text-card-foreground">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(async (data) => {
            data.questions.forEach((question, idx) => {
              if (question.guidelines && question.guidelines.trim() !== "") {
                const updatedRubrics = question.rubrics
                  ? `${question.rubrics}\n\nGuidelines:\n${question.guidelines}`
                  : `Guidelines:\n${question.guidelines}`;
                form.setValue(`questions.${idx}.rubrics`, updatedRubrics);
              }
            });

            const { id } = await handleSubmit(data);
            navigate({
              to: "/dashboard/evaluate/new",
              search: {
                question_set_id: id,
              },
            });
          })}
        >
          <div className="w-full space-y-4">
            <h2 className="text-2xl font-bold">Create new question set</h2>
            <FormField
              control={form.control}
              name={`name`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="text"
                      className="max-w-96 bg-card text-card-foreground"
                      placeholder="Enter name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {fields.map((field, index) => (
              <Question
                key={field.id}
                index={index}
                field={field}
                form={form}
                remove={remove}
              />
            ))}
            <div className="flex w-full justify-center gap-4">
              <Button
                type="button"
                size="sm"
                onClick={() =>
                  append({
                    question: "",
                    reference_answer: "",
                    rubrics: "",
                    guidelines: "",
                    marks: 1,
                  })
                }
                className="flex items-center gap-1 border-2 border-[#020817] p-6"
              >
                <Plus className="size-4" />
                Add Question
              </Button>
              <Button
                type="submit"
                size="sm"
                className="flex items-center gap-1 border-2 border-transparent bg-green-500 p-6 hover:bg-green-700 hover:text-white"
                //disabled={!form.formState.isValid}
              >
                <SendIcon className="size-4" />
                Submit
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}

function Question({ index, field, form, remove }: QuestionProps) {
  return (
    <Card key={field.id} className="relative">
      <CardHeader className="flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg">Question {index + 1}</CardTitle>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={() => remove(index)}
          className="flex items-center gap-1"
        >
          <Trash2 className="h-4 w-4" />
          Remove
        </Button>
      </CardHeader>
      <CardContent className="w-full space-y-4">
        <div className="flex w-full flex-row gap-6">
          <FormField
            control={form.control}
            name={`questions.${index}.marks`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Marks</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    className="max-w-24 bg-gray-200 text-card-foreground"
                    min={1}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`questions.${index}.question`}
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Question</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Enter the question"
                    className="min-h-24 bg-gray-200 text-card-foreground"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex w-full flex-row gap-6">
          <FormField
            control={form.control}
            name={`questions.${index}.reference_answer`}
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Reference Answer</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Enter the reference answer"
                    className="min-h-24 bg-gray-200 text-card-foreground"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={`questions.${index}.rubrics`}
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Rubrics</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Enter the rubrics"
                    className="min-h-24 bg-gray-200 text-card-foreground"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name={`questions.${index}.guidelines`}
          render={({ field }) => (
            <FormItem className="w-full pb-2">
              <FormLabel className="flex items-center gap-2">
                Add Evaluation Guidelines
                <SparkleIcon className="size-6" />
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter the evaluation guidelines"
                  className="min-h-16 bg-gray-200 text-card-foreground"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
      <CardFooter className="flex justify-end pt-0"></CardFooter>
    </Card>
  );
}
